import { useCallback, useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  connectionRows,
  GetFavoriteIssuesDocument,
  type IssueRow,
  RemoveFavoriteDocument,
} from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/** The fields the module-menu Favorites rows render — the subset the
 * first-paint mirror persists. `stageId` is here because a project row's
 * leading glyph IS its stage: without it the first frame would paint a
 * fallback glyph and swap, which is the pop-in the mirror exists to prevent.
 * A `Pick` of the row alias, so it cannot drift from the schema. */
export type FavoriteSidebarRow = Pick<
  IssueRow,
  "issueId" | "name" | "workflowId" | "stageId"
>;

type FavoritesMirror = { rows: FavoriteSidebarRow[] };

const MIRROR_DEFAULTS: FavoritesMirror = { rows: [] };

export function useFavoriteIssues(opts: { skip?: boolean } = {}) {
  const { favoriteIds, favoritesKnown } = useWorkspaceData();
  const { activeWorkspace } = useWorkspace();

  // Per-workspace mirror of the rendered sidebar rows — favorites arrive over
  // two network hops (preferences → issue rows), so without it the Favorites
  // section pops in a beat after the shell paints and shoves the sections
  // below it down. Server wins once known; the mirror covers first paint.
  // Same pattern (and same per-workspace keying) as `use-nav-preferences`.
  const { state: mirror, update: updateMirror } =
    usePersistedState<FavoritesMirror>(
      `favorites-mirror:${activeWorkspace?.workspaceId ?? "none"}`,
      MIRROR_DEFAULTS,
    );

  const favoriteIssueIds = useMemo(() => [...favoriteIds], [favoriteIds]);

  const variables = useMemo(
    () => ({ favoriteIssueIds }),
    [favoriteIssueIds],
  );

  const { data, loading } = useQuery(GetFavoriteIssuesDocument, {
    variables,
    skip: favoriteIssueIds.length === 0 || (opts.skip ?? false),
    fetchPolicy: "cache-first",
  });

  const issues: IssueRow[] = useMemo(
    () => (favoriteIssueIds.length === 0 ? [] : connectionRows(data?.favoriteIssues) ?? []),
    [data, favoriteIssueIds],
  );

  // True once the server has answered BOTH hops: preferences resolved, and —
  // when any ids exist — the issue rows landed. Only then may the mirror be
  // overwritten; an in-flight empty result must not wipe it.
  const serverKnown =
    favoritesKnown && (favoriteIssueIds.length === 0 || data !== undefined);

  useEffect(() => {
    if (!serverKnown) return;
    const next = issues.map(({ issueId, name, workflowId, stageId }) => ({
      issueId,
      name,
      workflowId,
      stageId,
    }));
    if (JSON.stringify(next) !== JSON.stringify(mirror.rows)) {
      updateMirror({ rows: next });
    }
  }, [serverKnown, issues, mirror.rows, updateMirror]);

  // What the module-menu Favorites section renders: live rows once known,
  // last-known mirrored rows before that. A mirrored name can be stale — it
  // corrects in place when the live rows land, with no layout shift.
  const sidebarRows: FavoriteSidebarRow[] = serverKnown ? issues : mirror.rows;

  const [removeMutation] = useMutation(RemoveFavoriteDocument);

  const removeFavorite = useCallback(
    async (issueId: string) => {
      try {
        await removeMutation({
          variables: { type: "issue", referenceId: issueId },
        });
      } catch (err) {
        toastMutationError(err, "Failed to remove from favorites");
      }
    },
    [removeMutation],
  );

  return {
    issues,
    sidebarRows,
    favoriteIds,
    isLoading: loading && issues.length === 0,
    removeFavorite,
  };
}
